import { Injectable } from "@angular/core";
import { BehaviorSubject, catchError, Observable, of, tap } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { CreateMessageInterface, MessageInterface } from "../interfaces/message-interface";

@Injectable({
    providedIn: "root",
})
export class TicketMessageService {
    private _messages: BehaviorSubject<MessageInterface[]> = new BehaviorSubject<MessageInterface[]>([]);
    private _message: BehaviorSubject<MessageInterface | null> = new BehaviorSubject(null);

    constructor(
        private _httpClient: HttpClient,
        private _toastr: ToastrService,
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Observable para la lista de messages
     */
    get messages$(): Observable<MessageInterface[]> {
        return this._messages.asObservable();
    }

    /**
     * Observable para un message específico
     */
    get message$(): Observable<MessageInterface | null> {
        return this._message.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * List all messages of a ticket
     */
    list(ticket_id: string): Observable<MessageInterface[]> {
        return this._httpClient.get<any[]>(`/api/v1/tickets/${ticket_id}/messages`).pipe(
            tap((messages) => this._messages.next(messages)),
            catchError((error) => this._handleError(error, "Error al listar los messages")),
        );
    }

    /**
     * Obtener un message por ID
     */
    show(ticket_id: string, message_id: string): Observable<MessageInterface> {
        return this._httpClient.get<any>(`/api/v1/tickets/${ticket_id}/messages/${message_id}`).pipe(
            tap((message) => this._message.next(message)),
            catchError((error) => this._handleError(error, "Error al obtener el message")),
        );
    }

    create(ticket_id: string, message: CreateMessageInterface): Observable<MessageInterface> {
        return this._httpClient.post<any>(`/api/v1/tickets/${ticket_id}/messages`, message).pipe(
            tap(() => this.list(ticket_id).subscribe()),
            catchError((error) => this._handleError(error, "Error al crear el message")),
        );
    }

    delete(message_id: string): Observable<any> {
        return this._httpClient
            .delete<any>(`/api/v1/messages/${message_id}`)
            .pipe(catchError((error) => this._handleError(error, "Delete message error")));
    }

    private _formatParams(data: any): { [key: string]: any } | FormData {
        const hasFile: boolean = Object.values(data).some((value) => value instanceof File);

        if (hasFile) {
            const formData: FormData = new FormData();

            Object.keys(data).forEach((key: string) => {
                const value = data[key];
                if (value instanceof File) {
                    formData.append(key, value, value.name);
                } else if (Array.isArray(value)) {
                    value.forEach((item, index) => {
                        formData.append(`${key}[${index}]`, String(item));
                    });
                } else if (typeof value === "object" && value !== null) {
                    formData.append(key, JSON.stringify(value));
                } else {
                    formData.append(key, String(value));
                }
            });

            return formData;
        }

        return data;
    }
    /**
     * Manejar errores y mostrar notificaciones
     */
    private _handleError(error: any, custommessage: string): Observable<any> {
        const errormessage = error?.error?.message || "Error desconocido";
        this._toastr.error(`${custommessage}: ${errormessage}`);
        return of(null);
    }
}
